import * as moment from 'moment';
import { IStrongBoxFilter } from "@nkr/models/strong-box/strong-box-filter";
import { ColDef, ColGroupDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClassParams, ValueGetterParams } from "ag-grid-community";
import { useMemo, useRef, useState } from "react";
import { IStrongBoxListing, IStrongBoxOrderContract } from "@nkr/models/strong-box";
import { Alert, Col, Row } from "react-bootstrap";
import { GridWrapper } from './styles/styles'
import { AgGridReact } from "ag-grid-react";
import { LoadingIndicator } from "@nkr/components/loading-indicator";
import { TLegendEntry } from "./legend";
import SortableHeaderComponent from "../recipients/recipient-shield/components/sortable-header";
import useStrongBoxCenters from "@nkr/store/strong-box/hooks/use-strong-box-centers";
import StrongBoxFilter from "./strong-box-filter";
import PlaceOrder, { EntityType } from "./place-order";
import { Utility } from '@nkr/services/utility';

type ColumnDef = ColDef | ColGroupDef;

const StrongBoxCenters = () => {
    const [filter, setFilter] = useState<IStrongBoxFilter>({});
    const { list, loading, error, placeOrder } = useStrongBoxCenters(filter)

    if (error) {
        return <Alert bsStyle="danger">error</Alert>
    }

    const handlePlaceOrder = (entityId: number, quantity: number, notes: string) => {
        let order: IStrongBoxOrderContract = {
            centerId: entityId,
            quantity,
            notes
        };

        placeOrder(order);
    };

    const colDefs: ColumnDef[] = [
        {
            colId: 'rNum',
            field: 'rNum',
            headerName: 'Center',
            sortable: false,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            width: 20,
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
            width: 120
        },
        {
            groupId: 'physicalInventory',
            headerName: 'Physical Inventory',
            headerClass: 'my-group-header',
            children: [
                {
                    colId: 'lastPhysicalInventoryDate',
                    field: 'lastPhysicalInventoryDate',
                    headerName: 'Last Report Date of Physical Inventory',
                    width: 100,
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Report Date</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        const d: IStrongBoxListing = params.data;

                        if (!d.lastPhysicalInventoryDate) {
                            return <>NA</>
                        }

                        return <>{moment(d.lastPhysicalInventoryDate).format('MM/DD/YYYY')}</>
                    },
                    comparator: Utility.dateComparator
                },
                {
                    colId: 'lastPhysicalInventoryCount',
                    field: 'lastPhysicalInventoryCount',
                    headerName: 'Physical Inventory Reported',
                    width: 100,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Reported Count</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        const d: IStrongBoxListing = params.data;

                        return (
                            <>{!d.lastPhysicalInventoryCount && d.lastPhysicalInventoryCount !== 0 ? 'NA' : d.lastPhysicalInventoryCount}</>
                        );
                    },
                },
            ]
        },
        {
            groupId: 'sinceLastPhysicalInventory',
            headerName: 'Since Last Report',
            headerClass: 'my-group-header',
            children: [
                {
                    colId: 'unitsOrderedSinceLastPhysicalInventory',
                    field: 'unitsOrderedSinceLastPhysicalInventory',
                    headerName: 'Units Ordered Since Last Report',
                    width: 120,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Units Ordered</SortableHeaderComponent>,
                },
                {
                    colId: 'unitsDeliveredSinceLastPhysicalInventory',
                    field: 'unitsDeliveredSinceLastPhysicalInventory',
                    headerName: 'Units Delivered Since Last Report',
                    width: 120,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Units Delivered</SortableHeaderComponent>,
                },
                {
                    colId: 'unitsUtilizedSinceLastPhysicalInventory',
                    field: 'unitsUtilizedSinceLastPhysicalInventory',
                    headerName: 'Units Utilized Since Last Report',
                    width: 120,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Units Utilized</SortableHeaderComponent>,
                },
            ]
        },
        {
            groupId: 'inventory',
            headerName: 'Inventory',
            headerClass: 'my-group-header',
            children: [
                {
                    colId: 'quantityOnHand',
                    field: 'quantityOnHand',
                    headerName: 'On Hand Inventory',
                    width: 90,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>On Hand</SortableHeaderComponent>,
                },
                {
                    colId: 'minimumInventoryLevel',
                    field: 'minimumInventoryLevel',
                    headerName: 'Minimum Inventory Level',
                    width: 100,
                    aggFunc: 'sum',
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Minimum Level</SortableHeaderComponent>,
                },
            ]
        },
        {
            colId: 'action',
            field: '',
            headerName: '',
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxListing = params.data;

                return (
                    <PlaceOrder
                        loading={loading}
                        entityType={EntityType.Center}
                        entityId={d.centerId}
                        entityName={d.centerAlias}
                        isShippingAddressCompliant={d.isShippingAddressCompliant}
                        onPlaceOrder={handlePlaceOrder}
                    />
                )
            },
            width: 52
        },
    ];

    const columnDefs = useMemo(() => colDefs, []);

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const expCsv = () => {
        // Call the API method to trigger download
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: `StrongBoxCenters.csv`,
                columnKeys: [
                    'centerName',
                    'lastPhysicalInventoryDate',
                    'lastPhysicalInventoryCount',
                    'unitsOrderedSinceLastPhysicalInventory',
                    'unitsDeliveredSinceLastPhysicalInventory',
                    'unitsUtilizedSinceLastPhysicalInventory',
                    'minimumInventoryLevel'
                ],
            });
        }
    };

    const getRowStyle = (params: RowClassParams) => {
        const d: IStrongBoxListing = params.data;
        if (d.quantityOnHand < d?.minimumInventoryLevel) {
            return { background: 'yellow' };
        }
    }

    const legendEntries: TLegendEntry[] = [
        { text: 'On hand inventory is below minimum level', color: 'yellow' }
    ];

    return (<>
        <Row>
            <Col sm={12}>
                <StrongBoxFilter
                    filter={filter}
                    onExport={expCsv}
                    defaultFilter={{}}
                    legendEntries={legendEntries}
                    isCenter={true}
                    onChange={setFilter}
                />
            </Col>
        </Row>

        {loading && <LoadingIndicator type='spin' color='black' loading={true} message={"Loading Inventory ..."} />}

        {!loading &&
            <Row>
                <Col sm={12}>
                    <GridWrapper className='ag-theme-bootstrap'>
                        <AgGridReact
                            defaultColDef={{
                                headerClass: 'list-header',
                                cellClass: 'list-cell',
                                sortable: true
                            }}
                            columnDefs={columnDefs}
                            rowData={list}
                            headerHeight={30}
                            groupHeaderHeight={30}
                            rowHeight={32}
                            noRowsOverlayComponentFramework={() => <div>No StrongBox Center records were found...</div>}
                            suppressRowClickSelection={true}
                            suppressCellSelection={true}
                            enableCellTextSelection={true}
                            onGridReady={onGridReady}
                            onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                            onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                            onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                            onSortChanged={(e) => e.api.refreshCells()}
                            getRowStyle={getRowStyle}
                        />
                    </GridWrapper>
                </Col>
            </Row>
        }
    </>);
}

export default StrongBoxCenters;